import React, { useState, useEffect, useCallback } from 'react';
import { 
  Link2, Server, RefreshCw, Check, X, AlertTriangle, Clock,
  Database, Users, FileText, ShoppingCart, TrendingUp,
  ArrowRight, Loader2, ChevronRight, Settings, Zap,
  Building2, Globe, Shield, Activity
} from 'lucide-react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';

// ERP logos and colors
const ERP_CONFIG = {
  sap: {
    name: 'SAP S/4HANA',
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    logo: '🔷',
    description: 'Enterprise resource planning for finance, logistics, and procurement'
  },
  ariba: {
    name: 'SAP Ariba Network',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-200',
    logo: '🔶',
    description: 'Procurement network with supplier sustainability intelligence'
  },
  coupa: {
    name: 'Coupa Procurement',
    color: 'bg-purple-600',
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    logo: '💜',
    description: 'Cloud spend management with ESG risk scoring'
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    connected: { color: 'bg-emerald-100 text-emerald-700', icon: Check, label: 'Connected' },
    disconnected: { color: 'bg-slate-100 text-slate-600', icon: X, label: 'Not Connected' },
    pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending' },
    syncing: { color: 'bg-blue-100 text-blue-700', icon: RefreshCw, label: 'Syncing' },
    error: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Error' }
  };
  
  const config = statusConfig[status] || statusConfig.disconnected;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon size={12} className={status === 'syncing' ? 'animate-spin' : ''} />
      {config.label}
    </span>
  );
};

// Data type icon mapping
const DataTypeIcon = ({ type }) => {
  const icons = {
    suppliers: Users,
    purchase_orders: ShoppingCart,
    invoices: FileText,
    contracts: FileText,
    spend_data: TrendingUp,
    emissions_data: Activity
  };
  const Icon = icons[type] || Database;
  return <Icon size={16} />;
};

// Connector Card Component
const ConnectorCard = ({ connector, onConnect, onDisconnect, onSync, loading }) => {
  const config = ERP_CONFIG[connector.erp_type] || ERP_CONFIG.sap;
  const isConnected = connector.status === 'connected';
  const isSyncing = connector.status === 'syncing' || loading;
  
  return (
    <div className={`bg-white rounded-xl border-2 ${isConnected ? config.borderColor : 'border-slate-200'} p-6 transition-all hover:shadow-lg`}
         data-testid={`connector-card-${connector.erp_type}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 ${config.lightColor} rounded-xl flex items-center justify-center text-2xl`}>
            {config.logo}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{config.name}</h3>
            <p className="text-sm text-slate-500">{config.description}</p>
          </div>
        </div>
        <StatusBadge status={connector.status} />
      </div>
      
      {/* Stats (when connected) */}
      {isConnected && connector.stats && (
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
          {Object.entries(connector.stats).map(([key, value]) => (
            <div key={key} className="text-center">
              <p className="text-xl font-bold text-slate-900">{value}</p>
              <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      )}
      
      {/* Data Types */}
      <div className="mb-4">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Data Types</p>
        <div className="flex flex-wrap gap-2">
          {connector.enabled_data_types?.map(type => (
            <span key={type} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md text-xs">
              <DataTypeIcon type={type} />
              {type.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>
      
      {/* Last Sync Info */}
      {isConnected && connector.last_sync && (
        <div className="text-xs text-slate-500 mb-4 flex items-center gap-2">
          <Clock size={12} />
          Last sync: {new Date(connector.last_sync).toLocaleString()}
        </div>
      )}
      
      {/* Error Message */}
      {connector.error_message && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {connector.error_message}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex gap-2">
        {isConnected ? (
          <>
            <button
              onClick={() => onSync(connector.erp_type)}
              disabled={isSyncing}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 ${config.color} text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50`}
              data-testid={`sync-btn-${connector.erp_type}`}
            >
              {isSyncing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              onClick={() => onDisconnect(connector.erp_type)}
              disabled={isSyncing}
              className="px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              data-testid={`disconnect-btn-${connector.erp_type}`}
            >
              Disconnect
            </button>
          </>
        ) : (
          <button
            onClick={() => onConnect(connector.erp_type)}
            disabled={isSyncing}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 ${config.color} text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50`}
            data-testid={`connect-btn-${connector.erp_type}`}
          >
            <Link2 size={16} />
            Connect
          </button>
        )}
      </div>
    </div>
  );
};

// Synced Data Table Component
const SyncedDataTable = ({ data, erpType, onMap }) => {
  const [selectedTab, setSelectedTab] = useState('suppliers');
  const config = ERP_CONFIG[erpType] || ERP_CONFIG.sap;
  
  const filteredData = data.filter(d => d.data_type === selectedTab && d.erp_type === erpType);
  
  const tabs = [
    { id: 'suppliers', label: 'Suppliers', icon: Users },
    { id: 'purchase_orders', label: 'Purchase Orders', icon: ShoppingCart },
    { id: 'spend_data', label: 'Spend Analytics', icon: TrendingUp },
  ];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const count = data.filter(d => d.data_type === tab.id && d.erp_type === erpType).length;
          return (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                selectedTab === tab.id 
                  ? `${config.textColor} border-b-2 ${config.borderColor.replace('border-', 'border-b-')}`
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={16} />
              {tab.label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  selectedTab === tab.id ? config.lightColor : 'bg-slate-100'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Table Content */}
      <div className="overflow-x-auto">
        {filteredData.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Database size={32} className="mx-auto mb-2 opacity-40" />
            <p>No {selectedTab.replace(/_/g, ' ')} synced yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">External ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                {selectedTab === 'suppliers' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Country</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Risk</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">ESG Score</th>
                  </>
                )}
                {selectedTab === 'purchase_orders' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                  </>
                )}
                {selectedTab === 'spend_data' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Spend</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Emissions</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">High Risk %</th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Synced</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.slice(0, 10).map((record, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-mono text-slate-600">{record.external_id}</td>
                  <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                    {record.data?.name || record.data?.category || '-'}
                  </td>
                  {selectedTab === 'suppliers' && (
                    <>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <Globe size={14} />
                          {record.data?.country_name || record.data?.country}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          record.data?.country_risk === 'high' ? 'bg-red-100 text-red-700' :
                          record.data?.country_risk === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {record.data?.country_risk || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {record.data?.esg_score || record.data?.sustainability_score ? (
                          <span className="font-medium">{record.data?.esg_score || record.data?.sustainability_score}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    </>
                  )}
                  {selectedTab === 'purchase_orders' && (
                    <>
                      <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                        ${record.data?.amount?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          record.data?.status === 'Closed' ? 'bg-emerald-100 text-emerald-700' :
                          record.data?.status === 'Open' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {record.data?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{record.data?.category}</td>
                    </>
                  )}
                  {selectedTab === 'spend_data' && (
                    <>
                      <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                        ${record.data?.spend_amount?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {record.data?.estimated_emissions_tco2e?.toLocaleString()} tCO2e
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          record.data?.high_risk_supplier_pct > 25 ? 'bg-red-100 text-red-700' :
                          record.data?.high_risk_supplier_pct > 15 ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {record.data?.high_risk_supplier_pct}%
                        </span>
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(record.synced_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Map to Disclosure OS Action */}
      {filteredData.length > 0 && selectedTab === 'suppliers' && (
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-sm text-slate-600">
            Map {filteredData.length} suppliers to Disclosure OS Value Chain
          </span>
          <button
            onClick={() => onMap(erpType, selectedTab)}
            className={`flex items-center gap-2 px-4 py-2 ${config.color} text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium`}
            data-testid={`map-btn-${erpType}`}
          >
            <ArrowRight size={16} />
            Map to Value Chain
          </button>
        </div>
      )}
    </div>
  );
};

// Main ERP Connectors Page
const ERPConnectors = () => {
  const [connectors, setConnectors] = useState([]);
  const [syncedData, setSyncedData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [notification, setNotification] = useState(null);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    const [connectorsData, dataData, summaryData] = await Promise.all([
      apiFetch(`${API}/erp/connectors`),
      apiFetch(`${API}/erp/data`),
      apiFetch(`${API}/erp/summary`)
    ]);

    if (connectorsData?._error || dataData?._error || summaryData?._error) {
      console.error('Error fetching ERP data:', connectorsData?.message || dataData?.message || summaryData?.message);
      showNotification('Error loading ERP connectors', 'error');
      setLoading(false);
      return;
    }

    setConnectors(connectorsData.connectors || []);
    setSyncedData(dataData.data || []);
    setSummary(summaryData);
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };
  
  // Connect to ERP
  const handleConnect = async (erpType) => {
    setActionLoading(prev => ({ ...prev, [erpType]: true }));
    const data = await apiFetch(`${API}/erp/connect/${erpType}`, {
      method: 'POST',
      body: { erp_type: erpType, use_oauth: true }
    });

    if (data?._error) {
      showNotification(data.message || 'Connection failed', 'error');
    } else if (data.success) {
      showNotification(`Successfully connected to ${ERP_CONFIG[erpType]?.name}`);
      await fetchData();
    } else {
      showNotification(data.error || 'Connection failed', 'error');
    }
    setActionLoading(prev => ({ ...prev, [erpType]: false }));
  };
  
  // Disconnect from ERP
  const handleDisconnect = async (erpType) => {
    if (!window.confirm(`Disconnect from ${ERP_CONFIG[erpType]?.name}? This will clear all synced data.`)) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [erpType]: true }));
    const data = await apiFetch(`${API}/erp/disconnect/${erpType}`, { method: 'POST', body: {} });

    if (data?._error) {
      showNotification('Disconnect failed', 'error');
    } else if (data.success) {
      showNotification(`Disconnected from ${ERP_CONFIG[erpType]?.name}`);
      setSelectedConnector(null);
      await fetchData();
    }
    setActionLoading(prev => ({ ...prev, [erpType]: false }));
  };
  
  // Sync ERP data
  const handleSync = async (erpType) => {
    setActionLoading(prev => ({ ...prev, [erpType]: true }));
    const data = await apiFetch(`${API}/erp/sync/${erpType}`, { method: 'POST', body: {} });

    if (data?._error) {
      showNotification(data.message || 'Sync failed', 'error');
    } else if (data.success) {
      showNotification(`Synced ${data.records_synced} records from ${ERP_CONFIG[erpType]?.name}`);
      await fetchData();
    }
    setActionLoading(prev => ({ ...prev, [erpType]: false }));
  };
  
  // Map to Disclosure OS
  const handleMap = async (erpType, dataType) => {
    setActionLoading(prev => ({ ...prev, [`map_${erpType}`]: true }));
    const data = await apiFetch(`${API}/erp/map-to-disclosure/${erpType}/${dataType}`, { method: 'POST', body: {} });

    if (data?._error) {
      showNotification('Mapping failed', 'error');
    } else if (data.success) {
      showNotification(`Mapped ${data.mapped_count} ${dataType} to Disclosure OS`);
    }
    setActionLoading(prev => ({ ...prev, [`map_${erpType}`]: false }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  
  return (
    <div className="space-y-8" data-testid="erp-connectors-page">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'
        } text-white`}>
          {notification.message}
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">ERP Connectors</h1>
          <p className="text-slate-600 mt-1">
            Connect your ERP systems to automatically sync supplier and procurement data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="refresh-all-btn"
          >
            <RefreshCw size={16} />
            Refresh All
          </button>
        </div>
      </div>
      
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Link2 className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.connected}</p>
                <p className="text-sm text-slate-500">Connected</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_suppliers}</p>
                <p className="text-sm text-slate-500">Suppliers Synced</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_records}</p>
                <p className="text-sm text-slate-500">Total Records</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.high_risk_suppliers}</p>
                <p className="text-sm text-slate-500">High Risk Suppliers</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Connector Cards */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Available Connectors</h2>
        <div className="grid grid-cols-3 gap-6">
          {connectors.map(connector => (
            <ConnectorCard
              key={connector.erp_type}
              connector={connector}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onSync={handleSync}
              loading={actionLoading[connector.erp_type]}
            />
          ))}
        </div>
      </div>
      
      {/* Synced Data Section */}
      {connectors.some(c => c.status === 'connected') && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Synced Data</h2>
          
          {/* Connector Tabs */}
          <div className="flex gap-2 mb-4">
            {connectors.filter(c => c.status === 'connected').map(connector => {
              const config = ERP_CONFIG[connector.erp_type];
              return (
                <button
                  key={connector.erp_type}
                  onClick={() => setSelectedConnector(connector.erp_type)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    selectedConnector === connector.erp_type
                      ? `${config.color} text-white`
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span>{config.logo}</span>
                  {config.name}
                </button>
              );
            })}
          </div>
          
          {/* Data Table */}
          {selectedConnector && (
            <SyncedDataTable
              data={syncedData}
              erpType={selectedConnector}
              onMap={handleMap}
            />
          )}
          
          {!selectedConnector && connectors.some(c => c.status === 'connected') && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Server size={32} className="mx-auto mb-2 text-slate-400" />
              <p className="text-slate-500">Select a connected ERP to view synced data</p>
            </div>
          )}
        </div>
      )}
      
      {/* Integration Guide */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Zap size={24} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">How ERP Integration Works</h3>
            <div className="grid grid-cols-4 gap-4 mt-4">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <span className="font-medium">Connect</span>
                </div>
                <p className="text-sm text-slate-300">Authenticate with your ERP using OAuth or API keys</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <span className="font-medium">Sync</span>
                </div>
                <p className="text-sm text-slate-300">Pull suppliers, POs, and spend data automatically</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <span className="font-medium">Map</span>
                </div>
                <p className="text-sm text-slate-300">Transform ERP data to Disclosure OS entities</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <span className="font-medium">Analyze</span>
                </div>
                <p className="text-sm text-slate-300">Use in Value Chain Risk and CSDDD modules</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Simulation Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-amber-800">Demo Mode</p>
          <p className="text-sm text-amber-700 mt-1">
            This integration uses simulated data for demonstration purposes. In production, connect to your actual SAP, Ariba, or Coupa instance using your enterprise credentials.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ERPConnectors;
